// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import { createApp, configureCompat } from 'vue'
import App from './App.vue'
import router from './router'
import store from './plugins/store'
import Util from './common/util';
import axios from 'axios'

import { Amplify, Auth } from 'aws-amplify'
import 'maplibre-gl/dist/maplibre-gl.css'

// axiosはデフォルトでタイムアウト無制限のため、通信が無応答のままローディングが固まるのを防ぐためグローバルに設定する
axios.defaults.timeout = 30000

// トークン有効期限切れ等で401が返った場合、セッション切れとして扱いサインイン画面へ誘導する
// 並列リクエストが同時に401になっても、通知・サインアウトは1回だけ行う
// Authorizationを付けずに送ったリクエストの401は、セッション切れではなく実装上の不備のため対象外（各画面のエラー表示に任せる）
let sessionExpiredHandling = false
axios.interceptors.response.use(
  response => response,
  async error => {
    const sentWithToken = !!(error.config && error.config.headers && error.config.headers.Authorization)
    if (error.response && error.response.status === 401 && sentWithToken && store.getters.user && !sessionExpiredHandling) {
      sessionExpiredHandling = true
      try {
        alert('セッションの有効期限が切れました。再度ログインしてください。')
        await Auth.signOut()
      } catch (signOutError) {
        console.error('サインアウトに失敗しました:', signOutError)
        store.commit('setUser', null)
      } finally {
        store.commit('setUserSignInStatus', false)
        sessionExpiredHandling = false
      }
    }
    return Promise.reject(error)
  }
)

// IDトークンの有効期限切れによる強制サインアウトを避けるため、期限切れ前にリフレッシュトークンで更新する
// （Auth.currentAuthenticatedUser/currentSessionは期限切れになるまで更新しないため、明示的にrefreshSessionを呼ぶ）
// ストアのuserはサインイン画面で作る簡易オブジェクト（メソッドなし）の場合があるため、
// 期限はJWTから読み取り、更新はAmplifyが保持する本物のCognitoUserに対して行う
const AUTH_CHECK_INTERVAL_MS = 60 * 1000
const AUTH_REFRESH_MARGIN_SEC = 5 * 60 // 有効期限の5分前から更新対象とする
let refreshingSession = null

function getStoredIdToken() {
  const user = store.getters.user
  return (user && user.signInUserSession && user.signInUserSession.idToken && user.signInUserSession.idToken.jwtToken) || null
}

function getJwtExpiration(jwt) {
  try {
    const payload = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(payload)).exp || null
  } catch (error) {
    return null
  }
}

async function refreshIdToken() {
  const cognitoUser = await Auth.currentAuthenticatedUser()
  const session = await new Promise((resolve, reject) => {
    cognitoUser.refreshSession(cognitoUser.getSignInUserSession().getRefreshToken(), (err, newSession) => (err ? reject(err) : resolve(newSession)))
  })
  const newToken = session.getIdToken().getJwtToken()
  const user = store.getters.user
  if (user && user !== cognitoUser && user.signInUserSession) {
    // 簡易オブジェクトの場合は、各画面が参照するIDトークンを差し替える
    store.commit('setUser', { ...user, signInUserSession: { ...user.signInUserSession, idToken: { jwtToken: newToken } } })
  }
}

// 期限が近い（または切れた）場合にIDトークンを更新する。更新した場合はtrueを返す
function refreshSessionIfNeeded() {
  const token = getStoredIdToken()
  const exp = token && getJwtExpiration(token)
  if (!exp || exp - Math.floor(Date.now() / 1000) > AUTH_REFRESH_MARGIN_SEC) return Promise.resolve(false)
  if (!refreshingSession) {
    refreshingSession = refreshIdToken().then(() => true).finally(() => {
      refreshingSession = null
    })
  }
  return refreshingSession
}

// 期限が近い（または切れた）トークンでAPIを呼ぶ前に更新し、リクエストのAuthorizationを差し替える
axios.interceptors.request.use(async config => {
  const oldToken = getStoredIdToken()
  if (oldToken && config.headers && config.headers.Authorization === oldToken) {
    try {
      if (await refreshSessionIfNeeded()) {
        config.headers.Authorization = getStoredIdToken()
      }
    } catch (error) {
      console.error('セッションの更新に失敗しました:', error.message)
    }
  }
  return config
})

setInterval(async () => {
  try {
    await refreshSessionIfNeeded()
  } catch (error) {
    console.error('セッションの自動更新に失敗しました:', error.message)
  }
}, AUTH_CHECK_INTERVAL_MS)

// Bootstrap Vue Next - 個別コンポーネントをインポート
import bootstrapVuePlugin from './plugins/bootstrap-vue.js'
import './assets/css/custom.css'

import "./assets/scss/global.scss"
import "./style.css"

// Vue 3互換モードを設定
configureCompat({
  MODE: 2,
  COMPONENT_V_MODEL: false,  // Vue 3のv-modelを使用
  COMPONENT_FUNCTIONAL: false, // Vue 3のfunctional componentを使用
  COMPONENT_ASYNC: false, // 非同期コンポーネントの互換警告を無効化
  WATCH_ARRAY: false, // 配列watchは適切にdeep:trueを設定済みのため無効化
  INSTANCE_ATTRS_CLASS_STYLE: false, // Bootstrap Vue Next内部のinheritAttrs警告を無効化
  ATTR_FALSE_VALUE: false, // false-value属性の処理方法変更警告を無効化
  OPTIONS_BEFORE_DESTROY: false, // beforeDestroy → beforeUnmount 名前変更警告を無効化
})

// Amplify v5.3.12設定（環境変数から）
// テスト用にダミー設定を使用
Amplify.configure({
  Auth: {
    region: 'ap-northeast-1',
    userPoolId: import.meta.env.VITE_COGNITO_USERPOOLID || 'dummy-pool-id',
    userPoolWebClientId: import.meta.env.VITE_COGNITO_USERPOOLWEBCLIENTID || 'dummy-client-id',
    identityPoolId: import.meta.env.VITE_COGNITO_IDENTITYPOOLID || 'dummy-identity-pool-id',
    authenticationFlowType: 'USER_PASSWORD_AUTH',
  }
})

let languageDict = {
  ja:{
      'Sign In Account' : 'アカウントにサインイン',
      'User Name' : 'ユーザ名',
      'Enter your User Name' : 'ユーザ名を入力してください',
      'Password' : 'パスワード',
      'Enter your password' : 'パスワードを入力してください',
      'Forgot your password? ' : ' ',
      'Reset password' : ' ',
      'No account?' : 'まだアカウントがない場合',
      'Back to Sign In' : 'サインイン画面に戻る',
      'Send Code' : '確認コードを送信',
      'Create account' : 'アカウント作成',
      'Sign In' : 'サインイン',
      'Sign Out' : 'サインアウト',
      'Username cannot be empty' : 'ユーザー名が入力されていません',
      'Incorrect username or password.' : 'ユーザー名もしくはパスワードが間違っています',
      'Custom auth lambda trigger is not configured for the user pool.' : 'パスワードが入力されていません',
      "2 validation errors detected: Value at 'userAlias' failed to satisfy constraint: Member must satisfy regular expression pattern: [\\p{L}\\p{M}\\p{S}\\p{N}\\p{P}]+; Value at 'userName' failed to satisfy constraint: Member must satisfy regular expression pattern: [\\p{L}\\p{M}\\p{S}\\p{N}\\p{P}]+" : "ユーザー名もしくはパスワードが間違っています",
      "Password attempts exceeded":"サインイン失敗上限回数を超えました。\r\n \
                                    しばらく経ってからサインインしてください。\r\n \
                                    サインインが出来ない場合は、\r\n \
                                    カスタマーサポートへお問い合わせください。"
  }
}

/* eslint-disable no-new */
const app = createApp(App)

// BootstrapVue3を個別コンポーネントプラグインとして使用
app.use(bootstrapVuePlugin)
app.mixin(Util)
app.use(router)
app.use(store)
app.mount('#app')
